import { cn, formatDateTime } from '@/lib/utils';

interface SignalMeta {
  snr?: number | null;
  spreadingFactor?: number | null;
  gatewayEui?: string | null;
  timestamp?: string | null;
}

interface SignalIconProps {
  rssi: number | null | undefined;
  meta?: SignalMeta;
  className?: string;
}

type Level = 0 | 1 | 2 | 3 | 4;

interface SignalLevel {
  level: Level;
  color: string;
  label: 'Excellent' | 'Good' | 'Fair' | 'Poor' | 'No signal';
}

// RSSI thresholds line up with the device-readings tab so the same dBm value
// reads the same way wherever it shows up.
function levelFor(rssi: number | null | undefined): SignalLevel {
  if (rssi == null) return { level: 0, color: 'text-gray-400 dark:text-gray-500', label: 'No signal' };
  if (rssi >= -80) return { level: 4, color: 'text-success-500', label: 'Excellent' };
  if (rssi >= -95) return { level: 3, color: 'text-success-600', label: 'Good' };
  if (rssi >= -105) return { level: 2, color: 'text-warning-500', label: 'Fair' };
  if (rssi >= -115) return { level: 1, color: 'text-error-500', label: 'Poor' };
  return { level: 0, color: 'text-error-600', label: 'No signal' };
}

interface TooltipRow {
  k: string;
  v: string;
}

function buildTooltipRows(rssi: number | null | undefined, label: string, meta?: SignalMeta): TooltipRow[] {
  const rows: TooltipRow[] = [{ k: 'Signal', v: label }];
  if (rssi != null) rows.push({ k: 'RSSI', v: `${rssi} dBm` });
  if (meta?.snr != null) rows.push({ k: 'SNR', v: `${meta.snr} dB` });
  if (meta?.spreadingFactor != null) rows.push({ k: 'Spread', v: `SF${meta.spreadingFactor}` });
  if (meta?.gatewayEui) rows.push({ k: 'Gateway', v: meta.gatewayEui });
  if (meta?.timestamp) rows.push({ k: 'Measured', v: formatDateTime(meta.timestamp) });
  return rows;
}

export function SignalIcon({ rssi, meta, className }: SignalIconProps) {
  const { color, label } = levelFor(rssi);
  const rows = buildTooltipRows(rssi, label, meta);
  const a11yLabel = rows.map((r) => `${r.k}: ${r.v}`).join(', ');

  // Display the raw RSSI dBm value rather than a bar glyph — the number is
  // more useful at a glance to anyone debugging signal issues. Still color-
  // coded by level. CSS group-hover pops a details panel instantly; named
  // group (group/signal) keeps it from firing when surrounding interactive
  // containers (like the property card's <button>) are hovered.
  return (
    <span
      role="img"
      aria-label={a11yLabel}
      className={cn(
        'relative inline-flex shrink-0 items-center group/signal',
        'text-xs font-medium tabular-nums',
        color,
        className,
      )}
    >
      {rssi != null ? `${rssi} dBm` : '—'}
      <span
        role="tooltip"
        className={cn(
          'pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 z-50',
          'rounded-md px-2.5 py-1.5 text-[11px] leading-tight whitespace-nowrap shadow-lg',
          'bg-gray-900 text-gray-100 dark:bg-gray-700 dark:text-gray-50',
          'opacity-0 invisible transition-opacity duration-100',
          'group-hover/signal:opacity-100 group-hover/signal:visible',
        )}
      >
        {rows.map((r) => (
          <span key={r.k} className="flex gap-2 font-mono">
            <span className="text-gray-400 dark:text-gray-300">{r.k}</span>
            <span>{r.v}</span>
          </span>
        ))}
      </span>
    </span>
  );
}
